/**
 * SSH Service for handling SSH connections from the browser
 */
export class SSHService {
  /**
   * Opens a terminal with an SSH connection to the specified server
   * @param ip The IP address of the server
   * @param username The username for SSH login
   * @param password The password for SSH login
   * @returns A promise that resolves when the command is executed
   */
  static openSSHTerminal(ip: string, username: string, password: string): void {
    try {
      // Method 1: Using ssh:// protocol (works on some systems)
      const sshUrl = `ssh://${username}:${encodeURIComponent(password)}@${ip}`;
      
      // Method 2: Using a custom URI scheme that could be registered on the client machine
      // This would require a protocol handler to be registered on the client's system
      const customUrl = `washprotech-ssh://${username}:${encodeURIComponent(password)}@${ip}`;
      
      // Try the standard SSH URL first
      console.log(`Opening SSH connection to ${ip}`);
      
      // Create a hidden link and click it to open the protocol
      const link = document.createElement('a');
      link.href = sshUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Inform the user about alternative methods if the standard method doesn't work
      console.log('If the SSH connection does not open automatically, you can use the following command in your terminal:');
      console.log(`ssh ${username}@${ip}`);
    } catch (error) {
      console.error("Error opening SSH connection:", error);
      // Provide fallback instructions
      alert(`Please use the following command in your terminal:\nssh ${username}@${ip}\n\nPassword: ${password}`);
    }
  }
  
  /**
   * Generates a copy-pasteable SSH command for the user
   * @param ip The IP address of the server
   * @param username The username for SSH login
   * @returns The SSH command string
   */
  static getSSHCommand(ip: string, username: string): string {
    return `ssh ${username}@${ip}`;
  }
  
  /**
   * Copies the SSH command to the clipboard
   * @param ip The IP address of the server
   * @param username The username for SSH login
   * @returns A promise that resolves when the command is copied to clipboard
   */
  static async copySSHCommand(ip: string, username: string): Promise<void> {
    const command = this.getSSHCommand(ip, username);
    try {
      await navigator.clipboard.writeText(command);
      console.log('SSH command copied to clipboard');
    } catch (error) {
      console.error('Failed to copy SSH command to clipboard:', error);
    }
  }
} 